// External libraries
import React, { useEffect } from 'react';

// Internal modules
import {
  canPayerShipToBilling,
  isShippingCallbackRequiredAddressValid,
  // @ts-expect-error untyped imports
} from './utils';
import { useStoreState } from '@/store';
import { features, getContent } from '@/lib';
import { useShippingAddress } from './hooks';
import { logBlurOrFocusEvent } from '../Form/utils';
// @ts-expect-error untyped imports
import { shipToBillingChangeHandler } from './handlers';
import { Checkbox, ParentContainer } from '@/components/Common';
import { useShippingAddressCallbacks } from '@/hooks/shippingCallbacks';
import type { InputOnBlurChange, InputOnChange, InputOnFocus } from '@/types';
import { AddressFields } from './Components';
import { OnboardTerms } from '../OnboardTerms';
// @ts-expect-error
import { changeHandler } from '../Form/handlers';

type AddressFormProps = {
  blurHandler?: InputOnBlurChange;
  changeHandler: InputOnChange;
  focusHandler?: InputOnFocus;
  showCheckboxes: boolean;
  showShipToBilling: boolean;
};

export const AddressForm = ({
  showCheckboxes,
  showShipToBilling,
  ...props
}: AddressFormProps): JSX.Element => {
  const isForcedGuest = features.isActive('shouldDisableSignup');
  const shipToBilling = useStoreState(({ form }) => form.shipToBilling);
  const shippingAddress = useShippingAddress();
  const isReduxStoreHydrated = useStoreState(
    ({ ui }) => ui.isReduxStoreHydrated,
  );
  // Since we potentially have two different states for
  // shipping address, billing form w/ ship to billing checked and shipping
  // form w/o ship to billing checked, we need to ensure theres only one
  // instance of this hook where it can use the store to get the correct
  // shipping address state.  Therefore, it must exist at the Address
  // level and not the AddressFields level.  Keeping it at the Address comp
  // ensures there will only ever be one instance of the hook
  const onShippingAddressCallback = useShippingAddressCallbacks();

  useEffect(() => {
    // Invoke callback on page load and after the redux store is hydrated,
    // and whenever the `Shipping same as billing address` checkbox is updated.
    //
    // @NOTE: We need to fire the callback on page load in case the merchant
    // passes a shipping address to prefill.
    if (
      isReduxStoreHydrated &&
      isShippingCallbackRequiredAddressValid(shippingAddress)
    ) {
      onShippingAddressCallback();
    }
  }, [isReduxStoreHydrated, shipToBilling.value]);

  return (
    <>
      <AddressFields
        addressType="billing"
        {...props}
        onShippingCallback={onShippingAddressCallback}
      />

      {showCheckboxes && (
        <>
          {showShipToBilling && (
            <>
              {canPayerShipToBilling() && (
                <ParentContainer margin="0 0 24px">
                  <Checkbox
                    blurHandler={logBlurOrFocusEvent}
                    changeHandler={shipToBillingChangeHandler}
                    checked={shipToBilling.value}
                    focusHandler={logBlurOrFocusEvent}
                    id="shipToBilling"
                    name="shipToBilling"
                    label={getContent('fieldLabels.shipToBilling')}
                    required={true}
                  />
                </ParentContainer>
              )}

              {!shipToBilling.value && (
                <AddressFields
                  addressType="shipping"
                  {...props}
                  onShippingCallback={onShippingAddressCallback}
                />
              )}
            </>
          )}

          {isForcedGuest && (
            <OnboardTerms changeHandler={changeHandler} type="guest" />
          )}
        </>
      )}
    </>
  );
};
